"use client";

import { TextField, useFormFields } from "@payloadcms/ui";
import Link from "next/link";
import { TextFieldClient, TextFieldClientProps } from "payload";
import { useEffect, useMemo, useRef, useState } from "react";

export function TOCAdminBlock({ field, path, validate }: TextFieldClientProps) {
  const titleField = useFormFields(([fields]) => fields["title"]);
  const title = titleField?.value as string | undefined;

  const props: TextFieldClient = useMemo<TextFieldClient>(
    () => ({
      ...field,
      type: "text",
      admin: {
        ...field.admin,
        label: "Table of Contents",
        description: "This will generate a table of contents from sections in your content",
        autoComplete: false,
        placeholder: "Table of Contents will be generated automatically",
        rtl: false,
      },
    }),
    [field]
  );

  return (
    <>
      <TextField field={props} path={path} validate={validate} />
      <div className="mt-4 rounded-lg border border-[rgb(76,76,76)] bg-[rgb(28,28,28)] p-4">
        <h3 className="mt-0 mb-4 text-lg font-semibold text-[rgb(249,249,249)]">
          Table of Contents Preview
        </h3>
        <TableOfContents
          blockType="toc"
          title={title || "Contents"}
          headings={[
            { id: "example-section-1", text: "Example Section 1", level: 2 },
            { id: "example-subsection", text: "Example Subsection", level: 3 },
            { id: "example-section-2", text: "Example Section 2", level: 2 },
          ]}
        />
      </div>
    </>
  );
}

export type TOCBlockProps = {
  blockType: "toc";
  title?: string;
  headerDepth?: string;
};

type Heading = {
  id: string;
  text: string;
  level: number;
};

type Props = TOCBlockProps & {
  className?: string;
  headings?: Heading[];
};

export const TableOfContents: React.FC<Props> = ({
  className,
  title = "Contents",
  headings: propHeadings,
  headerDepth,
}: Props) => {
  const [headings, setHeadings] = useState<Heading[]>(propHeadings || []);
  const [isLoading, setIsLoading] = useState<boolean>(!propHeadings);
  const initialized = useRef<boolean>(false);

  // Number of skeleton items to show during loading
  const skeletonCount = 3;

  useEffect(() => {
    if (propHeadings?.length) {
      setHeadings(propHeadings);
      setIsLoading(false);
      return;
    }

    if (typeof window === "undefined" || initialized.current) return;

    initialized.current = true;
    setIsLoading(true);

    // Parse header depth range (e.g., "1-3" becomes min=1, max=3)
    const parseHeaderDepth = (depthStr?: string): { min: number; max: number } | null => {
      if (!depthStr) return null;

      // Check for range format (e.g., "1-3")
      const rangeMatch = depthStr.match(/^(\d+)-(\d+)$/);
      if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
        const min = parseInt(rangeMatch[1]);
        const max = parseInt(rangeMatch[2]);
        return { min, max };
      }

      // Check for single number format (e.g., "2")
      const singleMatch = depthStr.match(/^(\d+)$/);
      if (singleMatch && singleMatch[1]) {
        const level = parseInt(singleMatch[1]);
        return { min: level, max: level };
      }

      return null;
    };

    const headerDepthRange = parseHeaderDepth(headerDepth);

    // Extract headings from DOM
    const extractHeadingsFromDOM = () => {
      try {
        // If headerDepth is specified, extract from h1-h6 tags
        if (headerDepthRange) {
          const { min, max } = headerDepthRange;
          const headerSelectors = Array.from(
            { length: max - min + 1 },
            (_, i) => `h${min + i}`
          ).join(", ");

          const headers = Array.from(document.querySelectorAll(headerSelectors));

          if (headers.length === 0) {
            console.debug("TOC: No headers found in document");
            return [];
          }

          const extracted = headers
            .map((header, index) => {
              // Create an ID if none exists
              let id = header.id;
              if (!id) {
                id = `header-${index}`;
                header.id = id;
              }

              const text = (header.textContent || "").trim();
              const level = parseInt(header.tagName.substring(1));

              return { id, text, level };
            })
            .filter((heading) => heading.text && heading.text.trim() !== "");

          return extracted;
        }

        // Otherwise, fall back to section-based extraction
        const sections = Array.from(document.querySelectorAll(".content-section"));

        if (sections.length === 0) {
          console.debug("TOC: No content sections found in document");
          return [];
        }

        const extracted = sections
          .map((section) => {
            const id = section.id;
            if (!id) return null;

            const header = section.querySelector("h1, h2, h3, h4, h5, h6");
            const text = (header?.textContent || "").trim();

            let level = 2;
            const levelAttr = section.getAttribute("data-section-level");

            if (levelAttr && !isNaN(parseInt(levelAttr))) {
              level = parseInt(levelAttr);
            } else if (header) {
              level = parseInt(header.tagName.substring(1));
            }

            return { id, text, level };
          })
          .filter(
            (heading): heading is Heading =>
              heading !== null &&
              Boolean(heading.id) &&
              Boolean(heading.text) &&
              heading.text.trim() !== ""
          );

        return extracted;
      } catch (error) {
        console.error("Error extracting headings:", error);
        return [];
      }
    };

    const extracted = extractHeadingsFromDOM();
    setHeadings(extracted);
    setIsLoading(false);
  }, [propHeadings, headerDepth]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      // smooth scroll
      element.scrollIntoView({ behavior: "smooth" });

      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `#${id}`);
      }
    }
  };

  if (headings.length === 0 && !isLoading) {
    return null;
  }

  return (
    <nav className={`my-6 max-w-full py-6 ${className || ""}`} aria-label="Table of contents">
      <div className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        {isLoading
          ? // Skeleton loaders that match the exact structure and styling of the actual items
            Array.from({ length: skeletonCount }, (_, i) => (
              <div
                key={`skeleton-${i}`}
                className="relative rounded-lg border border-solid border-[rgb(76,76,76)] bg-[rgb(30,28,44)] p-4 transition-all duration-200"
              >
                <div className="mb-1 block h-5 w-24 animate-pulse rounded bg-gray-700 text-base font-semibold"></div>
                <div className="my-3 block h-4 w-full animate-pulse rounded bg-gray-700 text-sm font-normal"></div>
              </div>
            ))
          : headings.map((heading, index) => (
              <div
                key={heading.id}
                className={`relative rounded-lg border border-solid border-[rgb(76,76,76)] bg-[rgb(30,28,44)] p-4 transition-all duration-200 hover:border-blue-500 hover:shadow-lg`}
              >
                <Link
                  href={`#${heading.id}`}
                  onClick={(e) => handleClick(e, heading.id)}
                  className="block text-left no-underline"
                >
                  <span className="mb-1 block text-base font-semibold text-[rgb(84,100,208)]">
                    Section {index + 1}:
                  </span>
                  <span className="my-3 block text-sm font-normal text-white">{heading.text}</span>
                </Link>
              </div>
            ))}
      </div>
    </nav>
  );
};
